import { useState } from 'react';
import { cn } from '@/lib/cn';
import { Badge } from '@/ui/Badge';
import { Button, estiloDeBoton } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { SkeletonTarjetas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { useEncuestas, type EncuestaDeLaLista } from './api';
import { FormularioDeEncuesta } from './FormularioDeEncuesta';
import { Responder } from './Responder';
import { Resultados } from './Resultados';

/**
 * Encuestas — `#encuestas`, y `#encuestas/<id>` para responder una. Módulo 13.
 *
 * Una sola entrada del `PAGE_MAP` con dos mitades: quien responde ve la lista y
 * el formulario; quien tiene `encuestas:manage` ve además crear, editar y los
 * resultados.
 *
 * ## Lo que cambia
 *
 * - **La pantalla pregunta por el permiso y no por el rol.** El vanilla decidía
 *   con `Auth.getUser()?.role !== 'colaborador'` en tres sitios, y el servidor
 *   con `encuestas:manage`: un rol a medida con el permiso no veía ni el botón
 *   de crear ni los resultados, y uno sin él los veía y recibía un 403.
 * - **«Responder» es un enlace a la sub-ruta**, que es la que usa el muro. Antes
 *   repintaba el contenido sin tocar el hash, así que el Atrás del navegador se
 *   saltaba la lista y salía del módulo.
 * - **Una encuesta sin preguntas lo dice** en vez de ofrecer «Responder» y
 *   llevar a una pantalla que avisa de que no hay ninguna.
 * - Un fallo al cargar la lista era un estado vacío con «Error cargando
 *   encuestas» y sin reintentar.
 *
 * El resto —responder, los resultados, el formulario— cuenta lo suyo en su
 * archivo.
 */
export function Encuestas() {
  const id = Number(window._routeParams?.[0]);
  if (Number.isInteger(id) && id > 0) return <Responder id={id} />;
  return <Lista />;
}

function Lista() {
  const consulta = useEncuestas();
  // `encuestas:manage` en el servidor. `Auth.isStaff()` es el respaldo de las
  // sesiones que no traen permisos resueltos, como en el resto del portal.
  const puedeGestionar = Auth.can('encuestas', 'manage') || Auth.isStaff();
  const [editando, setEditando] = useState<EncuestaDeLaLista | null>(null);
  const [creando, setCreando] = useState(false);
  const [resultadosDe, setResultadosDe] = useState<number | null>(null);

  const encuestas = consulta.data ?? [];

  return (
    <>
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-6 font-bold text-brand-ink">
            <i className="bi bi-clipboard-check" aria-hidden="true" /> Encuestas
          </h1>
          <p className="mt-1 text-3 text-muted">Tu opinión construye el bienestar del equipo.</p>
        </div>
        {puedeGestionar && (
          <Button onClick={() => setCreando(true)}>
            <i className="bi bi-plus-circle" aria-hidden="true" /> Nueva encuesta
          </Button>
        )}
      </header>

      {consulta.isPending ? (
        <SkeletonTarjetas n={3} />
      ) : consulta.isError ? (
        <EstadoError
          err={consulta.error}
          que="las encuestas"
          onReintentar={() => void consulta.refetch()}
        />
      ) : !encuestas.length ? (
        <EstadoVacio
          icono="bi-clipboard-x"
          titulo="Todavía no hay encuestas"
          descripcion={puedeGestionar
            ? 'Crea la primera: el equipo la verá aquí y en el muro, si lo eliges.'
            : 'Cuando el equipo publique una, la verás aquí.'}
          accion={puedeGestionar ? { texto: 'Nueva encuesta', onClick: () => setCreando(true) } : undefined}
        />
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {encuestas.map((e) => (
            <li key={e.id} className="flex">
              <Tarjeta
                encuesta={e}
                puedeGestionar={puedeGestionar}
                onEditar={() => setEditando(e)}
                onResultados={() => setResultadosDe(e.id)}
              />
            </li>
          ))}
        </ul>
      )}

      {(creando || editando) && (
        <FormularioDeEncuesta
          encuesta={editando}
          onCerrar={() => { setCreando(false); setEditando(null); }}
        />
      )}
      {resultadosDe != null && (
        <Resultados encuestaId={resultadosDe} onCerrar={() => setResultadosDe(null)} />
      )}
    </>
  );
}

function Tarjeta({
  encuesta, puedeGestionar, onEditar, onResultados,
}: {
  encuesta: EncuestaDeLaLista;
  puedeGestionar: boolean;
  onEditar: () => void;
  onResultados: () => void;
}) {
  const vacia = encuesta.question_count === 0;
  return (
    <Card interactiva className="flex w-full flex-col">
      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <h2 className="min-w-0 text-4 font-bold text-brand-ink">{encuesta.title}</h2>
          {encuesta.already_responded && (
            <Badge tone="ok" className="shrink-0">
              <i className="bi bi-check-circle-fill" aria-hidden="true" /> Respondida
            </Badge>
          )}
        </div>

        {encuesta.description && (
          <p className="line-clamp-2 text-3 text-muted">{encuesta.description}</p>
        )}

        <p className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 text-2 text-muted">
          <span>
            <i className="bi bi-list-ol" aria-hidden="true" />{' '}
            {vacia ? 'Sin preguntas' : `${encuesta.question_count} pregunta${encuesta.question_count === 1 ? '' : 's'}`}
          </span>
          <span>
            <i className="bi bi-chat-square-dots" aria-hidden="true" />{' '}
            {encuesta.response_count} respuesta{encuesta.response_count === 1 ? '' : 's'}
          </span>
          <span className={cn(encuesta.is_anonymous ? 'text-info-ink' : 'text-warn-ink')}>
            <i
              className={encuesta.is_anonymous ? 'bi bi-shield-lock' : 'bi bi-person-vcard'}
              aria-hidden="true"
            />{' '}
            {encuesta.is_anonymous ? 'Anónima' : 'Identificada'}
          </span>
        </p>
      </div>

      <div className="flex flex-wrap gap-2 border-t border-line p-3">
        {encuesta.already_responded ? (
          <Button variant="ghost" size="sm" className="flex-1" disabled
            razonDeshabilitado="Solo se permite una respuesta por persona">
            <i className="bi bi-check2-all" aria-hidden="true" /> Ya respondiste
          </Button>
        ) : vacia ? (
          <Button variant="ghost" size="sm" className="flex-1" disabled
            razonDeshabilitado="Todavía no tiene preguntas">
            <i className="bi bi-hourglass" aria-hidden="true" /> Sin preguntas
          </Button>
        ) : (
          // Un enlace, no un botón: así entra en el historial y el Atrás del
          // navegador vuelve a la lista.
          <a
            href={`#encuestas/${encuesta.id}`}
            className={cn(estiloDeBoton({ size: 'sm' }), 'flex-1')}
          >
            <i className="bi bi-pencil-square" aria-hidden="true" /> Responder
          </a>
        )}

        {puedeGestionar && (
          <>
            <Button variant="secondary" size="sm" onClick={onResultados}>
              <i className="bi bi-bar-chart-fill" aria-hidden="true" /> Resultados
            </Button>
            <Button
              variant="ghost" size="sm" iconOnly
              aria-label={`Editar ${encuesta.title}`}
              onClick={onEditar}
            >
              <i className="bi bi-pencil" aria-hidden="true" />
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}
